import { useEffect, useState, useRef } from "react";
import { styled } from "@mui/material/styles";
import { IconWholeWordMatch } from "@components";
import { Box, TextField, ToggleButton, Tooltip } from "@mui/material";
import ToggleButtonGroup, { toggleButtonGroupClasses } from "@mui/material/ToggleButtonGroup";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@hooks/useDebounce";

import { boxStyles, inputStyles, buttonsBoxStyle } from "./styles";

const VALUE_FULL_MATCH = "fullMatch";

interface ISearchProps {
  initialQuery?: string;
  onSearch: (query: string, fullMatchFlag?: boolean) => void;
  searchText?: string;
  isDisabled?: boolean;
  maxWidth?: number;
  fullMatch?: boolean;
}

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    padding: 5,
    marginLeft: 5,
    border: 0,
    borderRadius: theme.shape.borderRadius,
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: 0,
    },
  },
}));

export const Search = ({
  initialQuery = "",
  onSearch,
  searchText = "Search...",
  isDisabled,
  maxWidth,
  fullMatch,
}: ISearchProps) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchOptions, setsSearchOptions] = useState<string[]>([]);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const isSearchStartedRef = useRef(false);

  useEffect(() => {
    // Prevent search on the first render
    if (isSearchStartedRef.current) {
      onSearch(debouncedSearchQuery, searchOptions.includes(VALUE_FULL_MATCH));
    } else {
      isSearchStartedRef.current = true;
    }
  }, [debouncedSearchQuery, searchOptions]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleClear = () => {
    setSearchQuery("");
  };

  const handleSearchOptions = (event: React.MouseEvent<HTMLElement>, newValue: string[]) => {
    setsSearchOptions(newValue as any);
  };

  const boxSx = {
    ...boxStyles,
    maxWidth: maxWidth ?? "100%",
  };

  const inputStylesSx: Record<string, any> = {
    ...inputStyles,
  };
  inputStylesSx["& .MuiInputBase-input"] = {
    ...inputStylesSx["& .MuiInputBase-input"],
    pr: fullMatch ? 7 : 3,
  };

  return (
    <Box sx={boxSx}>
      <MagnifyingGlassIcon width="16" height="16" />
      <TextField
        variant="outlined"
        placeholder={searchText}
        value={searchQuery}
        onChange={handleChange}
        sx={inputStylesSx}
        disabled={isDisabled}
        fullWidth
      />
      <Box sx={buttonsBoxStyle}>
        {searchQuery && !isDisabled && <XMarkIcon width="16" height="16" onClick={handleClear} />}

        {fullMatch ? (
          <StyledToggleButtonGroup
            size="small"
            value={searchOptions}
            onChange={handleSearchOptions}
            aria-label="text formatting"
          >
            <Tooltip title="Match whole word">
              <ToggleButton value={VALUE_FULL_MATCH}>
                <IconWholeWordMatch color="#949494" />
              </ToggleButton>
            </Tooltip>
          </StyledToggleButtonGroup>
        ) : null}
      </Box>
    </Box>
  );
};
